import ClearIcon from '@mui/icons-material/Clear'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import TimelineSharpIcon from '@mui/icons-material/TimelineSharp'
import { Box, IconButton, SxProps, Theme, Tooltip } from '@mui/material'
import JobNumberAudit from 'components/Bookings/Audit/JobNumberAudit'
import DisplayDate from 'components/Common/DisplayDates/DisplayDate'
import notifyPopup from 'components/Common/Notification/notifyPopup'
import { useCallback } from 'react'
import { BookingDetailsProps } from '../BookingStatusPage'
import expectedArrivalDisplay from '../Common/expectedArrivalDisplay'

const WAITING_FOR_PASSENGER_PROGRESS = new Set([
  'Confirmed',
  'Driver_Allocated',
  'OnRoute',
])

type InfoProps = {
  title: string
  value?: string
  tooltip?: string
  copyLabel?: string
  onCopy?: () => void
  sx?: SxProps<Theme>
  valueSx?: SxProps<Theme>
}

const Info = ({
  title,
  value,
  tooltip,
  copyLabel,
  onCopy,
  sx,
  valueSx,
}: InfoProps) => (
  <Box
    component="div"
    sx={{
      display: 'flex',
      flexDirection: 'row',
      gap: '5px',
      alignItems: 'flex-start',
      minWidth: 0,
      color: '#FEFDFD',
      ...sx,
    }}
  >
    <Box
      component="p"
      sx={{
        fontWeight: 'bold',
        textTransform: 'uppercase',
        padding: 0,
        margin: 0,
      }}
    >
      {title}
    </Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '4px',
        minWidth: 0,
      }}
    >
      <Tooltip title={tooltip || ''} placement="top">
        <Box
          component="p"
          sx={{
            padding: 0,
            margin: 0,
            minWidth: 0,
            ...valueSx,
          }}
        >
          {value}
        </Box>
      </Tooltip>
      {onCopy ? (
        <Tooltip title={copyLabel || `Copy ${title}`} placement="top">
          <IconButton
            size="small"
            onClick={onCopy}
            sx={{
              color: '#FEFDFD',
              padding: 0,
              marginTop: '1px',
              flexShrink: 0,
            }}
          >
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  </Box>
)

const Header = ({ selectedJob, setSelectedJob }: BookingDetailsProps) => {
  const copyToClipboard = useCallback(async (value: string, label: string) => {
    if (!value) return

    if (!navigator.clipboard?.writeText) {
      notifyPopup({
        type: 'danger',
        title: 'Clipboard unavailable',
        message: `Unable to copy ${label.toLowerCase()} in this browser context.`,
      })
      return
    }

    try {
      await navigator.clipboard.writeText(value)
      notifyPopup({
        type: 'success',
        title: 'Copied',
        message: `${label} copied to clipboard.`,
      })
    } catch (error) {
      console.warn(
        '[BookingDetails/Header] navigator.clipboard.writeText failed:',
        error,
      )
      notifyPopup({
        type: 'danger',
        title: 'Copy failed',
        message: `Unable to copy ${label.toLowerCase()}.`,
      })
    }
  }, [])

  const progress = String(selectedJob?.status?.progress ?? '')
  const expectedArrival =
    selectedJob && WAITING_FOR_PASSENGER_PROGRESS.has(progress)
      ? expectedArrivalDisplay(
          selectedJob?.status.progress,
          selectedJob?.when,
          selectedJob?.estimatedArrivalTime,
          selectedJob?.status.estimatedArrivalTime
            ? selectedJob.status.estimatedArrivalTime
            : 0,
          selectedJob.timeZone,
        )
      : undefined

  return (
    <Box
      component="div"
      sx={(theme) => ({
        width: '100%',
        padding: '10px 20px',
        backgroundColor: theme.palette.brand.dark,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '10px',
      })}
    >
      <Box
        component="div"
        sx={{
          flex: '1 1 auto',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          columnGap: '18px',
          rowGap: '6px',
        }}
      >
        <Info
          title="Ref:"
          value={selectedJob?.jobReference || ''}
          copyLabel="Copy Journey Reference"
          onCopy={() =>
            copyToClipboard(
              selectedJob?.jobReference || '',
              'Journey Reference',
            )
          }
        />
        <Info
          title="Supp Ref:"
          value={selectedJob?.supplier.supplierReference || ''}
          tooltip={selectedJob?.supplier.supplierReference || ''}
          copyLabel="Copy Supplier Reference"
          onCopy={() =>
            copyToClipboard(
              selectedJob?.supplier?.supplierReference || '',
              'Supplier Reference',
            )
          }
          valueSx={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        />
        <Info
          title="time/date:"
          value={
            selectedJob?.when
              ? DisplayDate({
                  date: selectedJob.when,
                  viewType: 'actual',
                  timeZone: selectedJob.timeZone,
                })
              : 'TBD'
          }
        />
        {expectedArrival ? <Info title="exp:" value={expectedArrival} /> : null}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignSelf: 'center' }}>
        <IconButton
          onClick={() =>
            JobNumberAudit({
              jobNumber: selectedJob?.id,
              bookingId: selectedJob?.bookingId,
            })
          }
          sx={{ color: '#fff', padding: 0, margin: 0, flexShrink: 0 }}
        >
          <TimelineSharpIcon />
        </IconButton>
        <IconButton
          onClick={() => setSelectedJob && setSelectedJob(null)}
          sx={{ color: '#fff', padding: 0, margin: 0, flexShrink: 0 }}
        >
          <ClearIcon />
        </IconButton>
      </Box>
    </Box>
  )
}

export default Header
